import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useWebsocket } from '@lanetalk/websocket-sdk/react';

import { normalizeWebsocketMatch } from '@/hooks/home/utils/swedishLeagueNormalizer';

const CHANNEL = 'global_leagues';
const WEBSOCKET_URL = import.meta.env.VITE_WEBSOCKET_URL;
const WEBSOCKET_TOKEN = import.meta.env.VITE_WEBSOCKET_TOKEN;

const getTimestamp = (dateLike) => {
  if (!dateLike) return 0;
  if (dateLike instanceof Date) return dateLike.getTime();
  const parsed = new Date(dateLike);
  return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
};

const extractMatchesFromPayload = (payload) => {
  if (!payload) {
    return { matches: [], isBatch: false };
  }

  const candidate = payload.result ?? payload;
  const data =
    candidate?.data ??
    candidate?.result?.data ??
    candidate?.matches ??
    candidate?.match;

  if (!data) {
    return { matches: [], isBatch: false };
  }

  if (Array.isArray(data)) {
    return { matches: data, isBatch: true };
  }

  return { matches: [data], isBatch: false };
};

export default function useOngoingSwedishMatches() {
  const canConnect = Boolean(WEBSOCKET_URL && WEBSOCKET_TOKEN);
  const wsRef = useRef();

  const matchesRef = useRef(new Map());

  const [ongoingMatches, setOngoingMatches] = useState([]);
  const [isLoading, setIsLoading] = useState(canConnect);
  const [error, setError] = useState(null);

  const {
    subscriptionData,
    lastMessage,
    error: wsError,
    isConnected,
  } = useWebsocket({
    url: WEBSOCKET_URL,
    channel: CHANNEL,
    apiKey: WEBSOCKET_TOKEN,
    enabled: canConnect,
    debug: Boolean(import.meta.env.DEV),
    ref: wsRef,
  });

  const upsertMatches = useCallback((rawMatches, { reset = false } = {}) => {
    const registry = matchesRef.current;
    if (reset) {
      registry.clear();
    }

    rawMatches.forEach((rawMatch) => {
      const normalized = normalizeWebsocketMatch(rawMatch);
      if (!normalized?.id) return;
      registry.set(normalized.id, normalized);
    });

    const nextMatches = Array.from(registry.values()).sort((a, b) => {
      return getTimestamp(b.scheduledAt) - getTimestamp(a.scheduledAt);
    });

    setOngoingMatches(nextMatches);
    setIsLoading(false);
  }, []);

  const processPayload = useCallback(
    (payload) => {
      const { matches, isBatch } = extractMatchesFromPayload(payload);

      if (matches.length === 0 && isBatch) {
        upsertMatches([], { reset: true });
        return;
      }

      if (matches.length === 0) return;

      upsertMatches(matches, { reset: isBatch });
    },
    [upsertMatches]
  );

  const refresh = useCallback(() => {
    if (!wsRef.current?.connection) return;
    setIsLoading(true);
    setError(null);
    try {
      wsRef.current.connection.disconnect();
      wsRef.current.connection.connect();
    } catch (refreshError) {
      console.error('useOngoingSwedishMatches', refreshError);
      setError(refreshError);
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (subscriptionData == null) return;

    const hasSubscriptionData = Object.keys(subscriptionData).length > 0;
    if (!hasSubscriptionData) {
      setIsLoading(false);
      return;
    }

    processPayload(subscriptionData);
  }, [subscriptionData, processPayload]);

  useEffect(() => {
    if (lastMessage) {
      processPayload(lastMessage);
    }
  }, [lastMessage, processPayload]);

  useEffect(() => {
    if (canConnect) return;
    setIsLoading(false);
    setError(null);
    matchesRef.current.clear();
    setOngoingMatches([]);
  }, [canConnect]);

  useEffect(() => {
    if (!wsError) return;
    setError(wsError);
    setIsLoading(false);
  }, [wsError]);

  const meta = useMemo(
    () => ({
      isLoading,
      hasError: Boolean(error),
      error,
      isConnected,
      refresh,
    }),
    [error, isConnected, isLoading, refresh]
  );

  return {
    ongoingMatches,
    ...meta,
  };
}
